import { useEffect, useRef, useState } from "react";
import { useDashboardData } from "../../../context/DashboardContext";
import { ResponsivePie } from "@nivo/pie";
import { useActiveTheme } from "../../../context/ThemeContext";
import type { WidgetComponentProps } from "../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

interface ChartDataItem {
  name: string;
  filterValue?: string;  // Real value for filtering (falls back to name)
  value: number;
  percentage?: number;
  revenue?: number;
}

const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  // Merge by display name, but keep track of filterValues
  const mergedMap = new Map<string, { value: number; revenue: number; filterValues: string[] }>();
  for (const item of data) {
    const existing = mergedMap.get(item.name);
    if (existing) {
      existing.value += item.value;
      existing.revenue += (item.revenue || 0);
      // Collect all filterValues for this display name
      if (item.filterValue && !existing.filterValues.includes(item.filterValue)) {
        existing.filterValues.push(item.filterValue);
      }
    } else {
      mergedMap.set(item.name, {
        value: item.value,
        revenue: item.revenue || 0,
        filterValues: item.filterValue ? [item.filterValue] : [item.name],
      });
    }
  }

  const merged = Array.from(mergedMap.entries()).map(([name, d]) => ({
    name,
    value: d.value,
    revenue: d.revenue,
    filterValue: d.filterValues.join(","),  // Join multiple values for filtering
  }));

  merged.sort((a, b) => b.value !== a.value ? b.value - a.value : a.name.localeCompare(b.name));

  const total = merged.reduce((sum, item) => sum + item.value, 0);

  return merged.map((item, index) => ({
    id: item.name,
    label: item.name,
    filterValue: item.filterValue,  // Include filterValue in pie data
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: datum.color }} />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

export default function PieChartWidget({ config, title }: WidgetComponentProps) {
  const PIE_COLORS = useActiveTheme().colors.chart;
  const { chartData, chartInteractions, rawData } = useDashboardData();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const dataKey = config.dataKey as string;
  const leadModeAware = Boolean(config.leadModeAware);
  const chartFilterMode = (rawData.chartFilterMode as "closed" | "all" | undefined) || "closed";
  const setChartFilterMode = rawData.setChartFilterMode as ((mode: "closed" | "all") => void) | undefined;
  const height = (config.height as number) || 350;
  const defaultMarginX = (config.marginX as number) || 120;
  const marginX = containerWidth > 0 && containerWidth < 480 ? Math.min(defaultMarginX, 40) : defaultMarginX;
  const marginY = (config.marginY as number) || 40;
  const colors = (config.colors as string[] | undefined) || PIE_COLORS;

  const chartItems = (chartData[dataKey] as ChartDataItem[] | undefined) || [];
  const pieData = toNivoPieData(chartItems, colors);
  const interaction = chartInteractions?.[dataKey];

  const displayTitle = leadModeAware
    ? ((chartFilterMode === "all" ? config.titleAll : config.titleClosed) as string | undefined) || title
    : title;

  if (pieData.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold">{displayTitle}</h3>
        </div>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-3 sm:p-6 h-full flex flex-col overflow-hidden">
      <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold">{displayTitle}</h3>
        {leadModeAware && setChartFilterMode && (
          <Select value={chartFilterMode} onValueChange={(value) => setChartFilterMode(value as "closed" | "all")}>
            <SelectTrigger className="h-8 w-[132px] text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="closed">Closed Leads</SelectItem>
              <SelectItem value="all">All Leads</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <div ref={containerRef} className="flex-1 min-w-0 min-h-0" style={{ minHeight: height }}>
        <ResponsivePie
          data={pieData}
          margin={{ top: marginY, right: marginX, bottom: marginY, left: marginX }}
          innerRadius={0.5}
          padAngle={1}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          sortByValue={false}
          colors={{ datum: "data.color" }}
          borderWidth={2}
          borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
          enableArcLinkLabels={containerWidth === 0 || containerWidth >= 480}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLinkLabel={(d) => {
            const label = String(d.label);
            return label.length > 25 ? label.slice(0, 22) + "..." : label;
          }}
          enableArcLabels={true}
          arcLabelsSkipAngle={10}
          arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
          arcLabelsTextColor="white"
          theme={getNivoPieTheme()}
          tooltip={PieChartTooltip}
          onClick={interaction ? (datum) => interaction.onSelect(String(datum.data.filterValue || datum.label)) : undefined}
          isInteractive={Boolean(interaction)}
          legends={[]}
        />
      </div>
    </div>
  );
}
